/**
 * useAdminSignIn - Custom hook for admin sign in functionality
 * 
 * Handles admin authentication with privilege check.
 * 
 * @example
 * ```tsx
 * const { signIn, isLoading, error } = useAdminSignIn();
 * 
 * await signIn({ email: "admin@example.com", password: "password123" });
 * ```
 */

import { useCallback, useState } from "react";
import { getSession, signIn, signOut } from "next-auth/react";
import { useSignIn, type SignInCredentials } from "./useSignIn";
import { getAuthErrorMessage } from "@/lib/utils/auth/error-messages";
import { hasAnyAdminRole } from "@/lib/utils/roles";
import type { AppUser } from "@/lib/types/next-auth";

export interface UseAdminSignInOptions {
  /**
   * Callback executed on successful sign in
   */
  onSuccess?: () => void;
  /**
   * Callback executed on error
   */
  onError?: (error: string) => void;
}

export interface UseAdminSignInReturn {
  /**
   * Sign in function
   */
  signIn: (credentials: SignInCredentials) => Promise<boolean>;
  /**
   * Loading state
   */
  isLoading: boolean;
  /**
   * Error message (user-friendly)
   */
  error: string | null;
  /**
   * Clear error manually
   */
  clearError: () => void;
}

export function useAdminSignIn(options?: UseAdminSignInOptions): UseAdminSignInReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleSignIn = useCallback(
    async (credentials: SignInCredentials): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        // First, attempt sign in
        const result = await signIn("credentials", {
          redirect: false,
          email: credentials.email,
          password: credentials.password,
        });

        if (result?.error) {
          const friendlyError = getAuthErrorMessage(result.error);
          setError(friendlyError);
          options?.onError?.(friendlyError);
          return false;
        }

        // Check admin dashboard access (admin, teacher, or staff)
        const session = await getSession();
        const user = session?.user as AppUser | undefined;
        if (!user || !hasAnyAdminRole(user)) {
          await signOut({ redirect: false });
          const accessError = "You do not have access to the admin dashboard.";
          setError(accessError);
          options?.onError?.(accessError);
          return false;
        }

        // Success
        options?.onSuccess?.();
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An unexpected error occurred. Please try again.";
        const friendlyError = getAuthErrorMessage(errorMessage);
        setError(friendlyError);
        options?.onError?.(friendlyError);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [options],
  );

  return {
    signIn: handleSignIn,
    isLoading,
    error,
    clearError,
  };
}

